import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import LoadingSpinner from '../../components/Common/LoadingSpinner';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';

const FileForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { get, post, put, loading } = useApi();
  
  const [formData, setFormData] = useState({
    typeId: '',
    path: '',
    size: '',
    hash: '',
    isEncrypted: false,
    encryptionKeyId: '',
    timeStamp: ''
  });

  const [fileTypes, setFileTypes] = useState([]);
  const [loadingTypes, setLoadingTypes] = useState(true);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchFileTypes();
    if (id) {
      fetchFile();
    }
  }, [id]);

  const fetchFileTypes = async () => {
    setLoadingTypes(true);
    const { data } = await get('/api/file-types');
    if (data) {
      setFileTypes(data);
      console.log('File Types loaded:', data);
    }
    setLoadingTypes(false);
  };

  const fetchFile = async () => {
    const { data } = await get(`/api/files/${id}`);
    if (data) {
      setFormData({
        typeId: data.type?.id || '',
        path: data.path || '',
        size: data.size || '',
        hash: data.hash || '',
        isEncrypted: data.isEncrypted || false,
        encryptionKeyId: data.encryptionKeyId || '',
        timeStamp: data.timeStamp ? data.timeStamp.slice(0, 16) : ''
      });
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.typeId) {
      newErrors.typeId = 'File type is required';
    }
    if (!formData.path.trim()) {
      newErrors.path = 'File path is required';
    }
    if (!formData.timeStamp) {
      newErrors.timeStamp = 'Timestamp is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    const submitData = {
      typeId: parseInt(formData.typeId),
      path: formData.path,
      size: formData.size ? parseInt(formData.size) : null,
      hash: formData.hash || null,
      isEncrypted: formData.isEncrypted,
      encryptionKeyId: formData.encryptionKeyId || null,
      timeStamp: formData.timeStamp
    };

    console.log('Submitting file data:', submitData);

    let response;
    if (id) {
      response = await put(`/api/files/${id}`, submitData);
    } else {
      response = await post('/api/files', submitData);
    }

    if (!response.error) {
      navigate('/files/filter');
    }
  };

  if ((loading && id) || loadingTypes) return <LoadingSpinner />;

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex items-center mb-6">
        <button
          onClick={() => navigate('/files/filter')}
          className="mr-4 text-gray-600 hover:text-gray-900"
        >
          <ArrowLeftIcon className="h-6 w-6" />
        </button>
        <h1 className="text-2xl font-bold text-gray-800">
          {id ? 'Edit File' : 'Upload New File'}
        </h1>
      </div>

      <div className="bg-white rounded-xl shadow-lg p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* File Type Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                File Type <span className="text-red-500">*</span>
              </label>
              <select
                name="typeId"
                value={formData.typeId}
                onChange={handleChange}
                className={`input-field ${errors.typeId ? 'border-red-500' : ''}`}
              >
                <option value="">Select file type</option>
                {fileTypes.map(type => (
                  <option key={type.id} value={type.id}>
                    {type.fileName} - {type.fileDescription}
                  </option>
                ))}
              </select>
              {errors.typeId && (
                <p className="mt-1 text-sm text-red-600">{errors.typeId}</p>
              )}
              {fileTypes.length === 0 && (
                <p className="mt-1 text-sm text-yellow-600">No file types found. Please create a file type first.</p>
              )}
            </div>

            {/* File Path */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                File Path <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="path"
                value={formData.path}
                onChange={handleChange}
                className={`input-field ${errors.path ? 'border-red-500' : ''}`}
                placeholder="/path/to/file.pdf"
              />
              {errors.path && (
                <p className="mt-1 text-sm text-red-600">{errors.path}</p>
              )}
            </div>

            {/* File Size */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                File Size (bytes)
              </label>
              <input
                type="number"
                name="size"
                value={formData.size}
                onChange={handleChange}
                className="input-field"
                placeholder="1024"
              />
            </div>

            {/* File Hash */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                File Hash
              </label>
              <input
                type="text"
                name="hash"
                value={formData.hash}
                onChange={handleChange}
                className="input-field"
                placeholder="SHA-256 hash"
              />
            </div>

            {/* Encryption Key ID */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Encryption Key ID
              </label>
              <input
                type="text"
                name="encryptionKeyId"
                value={formData.encryptionKeyId}
                onChange={handleChange}
                className="input-field"
                placeholder="Encryption key ID"
                disabled={!formData.isEncrypted}
              />
            </div>

            {/* Timestamp */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Timestamp <span className="text-red-500">*</span>
              </label>
              <input
                type="datetime-local"
                name="timeStamp"
                value={formData.timeStamp}
                onChange={handleChange}
                className={`input-field ${errors.timeStamp ? 'border-red-500' : ''}`}
              />
              {errors.timeStamp && (
                <p className="mt-1 text-sm text-red-600">{errors.timeStamp}</p>
              )}
            </div>

            {/* Is Encrypted */}
            <div className="md:col-span-2">
              <label className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  name="isEncrypted"
                  checked={formData.isEncrypted}
                  onChange={handleChange}
                  className="h-4 w-4 text-indigo-600 rounded"
                />
                <span className="text-sm font-medium text-gray-700">File is encrypted</span>
              </label>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-4 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={() => navigate('/files/filter')}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            >
              {loading ? 'Saving...' : id ? 'Update File' : 'Upload File'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FileForm;